const LEADER_KEY = 'shipcrm_sse_leader';
const TAB_ID_KEY = 'shipcrm_tab_id';
const CHANNEL_NAME = 'shipcrm-sse-relay';
const HEARTBEAT_MS = 2000;
const LEADER_TIMEOUT_MS = 5000;

const getTabId = () => {
  let id = sessionStorage.getItem(TAB_ID_KEY);
  if (!id) {
    id = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(TAB_ID_KEY, id);
  }
  return id;
};

const readLeader = () => {
  try {
    const raw = localStorage.getItem(LEADER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const isLeaderStale = (leader) => !leader || Date.now() - leader.ts > LEADER_TIMEOUT_MS;

const writeLeader = (tabId) => {
  localStorage.setItem(LEADER_KEY, JSON.stringify({ tabId, ts: Date.now() }));
};

const clearLeaderIfMine = (tabId) => {
  const leader = readLeader();
  if (leader && leader.tabId === tabId) {
    localStorage.removeItem(LEADER_KEY);
  }
};

export const supportsSharedSSE = () => typeof BroadcastChannel !== 'undefined';

const createCoordinator = () => {
  const tabId = getTabId();
  let channel = null;
  let heartbeatTimer = null;
  let isLeader = false;
  let started = false;
  let lastRelayedStatus = 'disconnected';
  let handlers = {};

  const setLeader = (next) => {
    if (isLeader === next) {
      return;
    }
    isLeader = next;
    if (next) {
      handlers.onBecomeLeader?.();
    } else {
      handlers.onBecomeFollower?.(lastRelayedStatus);
    }
  };

  const handleChannelMessage = (event) => {
    const { kind, payload, status } = event.data || {};
    if (kind === 'message') {
      handlers.onRelay?.(payload);
    } else if (kind === 'status') {
      lastRelayedStatus = status;
      handlers.onStatus?.(status);
    } else if (kind === 'leader_release') {
      tick();
    }
  };

  const tick = () => {
    if (!started) {
      return;
    }

    const leader = readLeader();

    if (isLeader) {
      if (leader && leader.tabId === tabId) {
        writeLeader(tabId);
        if (lastRelayedStatus !== 'disconnected') {
          channel?.postMessage({ kind: 'status', status: lastRelayedStatus });
        }
      } else if (leader && leader.tabId !== tabId && !isLeaderStale(leader)) {
        setLeader(false);
      } else {
        writeLeader(tabId);
      }
      return;
    }

    if (!leader || isLeaderStale(leader) || leader.tabId === tabId) {
      writeLeader(tabId);
      const verify = readLeader();
      if (verify && verify.tabId === tabId) {
        setLeader(true);
      }
    }
  };

  const onBeforeUnload = () => {
    if (isLeader) {
      clearLeaderIfMine(tabId);
      channel?.postMessage({ kind: 'leader_release' });
    }
  };

  return {
    init(nextHandlers) {
      handlers = nextHandlers || {};
      if (started) {
        tick();
        return;
      }

      started = true;
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = handleChannelMessage;
      window.addEventListener('beforeunload', onBeforeUnload);
      heartbeatTimer = setInterval(tick, HEARTBEAT_MS);
      tick();
      if (!isLeader) {
        handlers.onBecomeFollower?.(lastRelayedStatus);
      }
    },

    release() {
      if (!started) {
        return;
      }

      started = false;
      if (isLeader) {
        clearLeaderIfMine(tabId);
        channel?.postMessage({ kind: 'leader_release' });
      }
      isLeader = false;
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
      }
      if (channel) {
        channel.onmessage = null;
        channel.close();
        channel = null;
      }
      handlers = {};
    },

    relayMessage(payload) {
      channel?.postMessage({ kind: 'message', payload });
    },

    relayStatus(status) {
      lastRelayedStatus = status;
      channel?.postMessage({ kind: 'status', status });
    },

    isLeader() {
      return isLeader;
    },

    getRelayedStatus() {
      return lastRelayedStatus;
    },
  };
};

let coordinatorInstance = null;

export const getSSECoordinator = () => {
  if (!coordinatorInstance) {
    coordinatorInstance = createCoordinator();
  }
  return coordinatorInstance;
};
